import { useMemo } from 'react';
import { getSelectedSeatsTotal } from 'hooks/usePricingBeforeCheckout';

/**
 * Calculate the total price of selected seats
 * @param {array} selectedSeatsBeforeCheckout - Array of selected seats with price information
 * @param {number} total - Default total price per seat
 * @param {boolean} useSeatBasedPricing - Whether seat prices should drive totals
 * @returns {number} - The total price of all selected seats
 */
const calculateSelectedSeatsTotal = (
  selectedSeatsBeforeCheckout,
  total,
  useSeatBasedPricing,
  defaultPricing,
  mixDiscountFaresByAvailability,
) => {
  return getSelectedSeatsTotal({
    seats: selectedSeatsBeforeCheckout,
    defaultPricing: defaultPricing || { total },
    useSeatBasedPricing,
    mixDiscountFaresByAvailability,
  });
};

/**
 * Calculate the final total price based on configuration and selected seats
 * Handles multiple scenarios:
 * 1. If using seat-based pricing, return selectedSeatsTotal
 * 2. If it's a bulk ticket with monthly plans, return monthly payment
 * 3. If it's a bulk ticket without monthly plans, return global bulk total
 * 4. If displaying pricing before checkout, calculate from selected seats or return purchase total
 * 5. Fallback to the regular total price
 *
 * @param {object} options - Configuration options
 * @param {boolean} options.useSelectedSeats - Whether to use selected seats for pricing
 * @param {number} options.selectedSeatsTotal - Total amount for selected seats
 * @param {boolean} options.isBulkTicket - Whether this is a bulk ticket
 * @param {number} options.monthlySelectedPlan - Number of monthly plan selected
 * @param {object} options.selectedInstallmentsPlan - Selected installment plan object with monthlyPayment
 * @param {number} options.globalBulkTotal - Global bulk ticket total
 * @param {boolean} options.displayPricingBeforeCheckout - Whether to display pricing before checkout
 * @param {array} options.selectedSeatsBeforeCheckout - Selected seats before checkout
 * @param {number} options.purchaseTotalBeforeCheckout - Total price before checkout
 * @param {boolean} options.useSeatBasedPricing - Whether selected seat prices are authoritative
 * @param {object} options.defaultPricing - Pricing object used as the per-passenger fallback
 * @param {boolean} options.mixDiscountFaresByAvailability - Whether to mix discount and regular fares when availability is exceeded
 * @param {number} options.total - Default total price per seat
 * @returns {number} - The calculated final total price
 */
const usePricingTotal = ({
  useSelectedSeats,
  selectedSeatsTotal,
  isBulkTicket,
  monthlySelectedPlan,
  selectedInstallmentsPlan,
  globalBulkTotal,
  displayPricingBeforeCheckout,
  selectedSeatsBeforeCheckout,
  purchaseTotalBeforeCheckout,
  useSeatBasedPricing = false,
  defaultPricing,
  mixDiscountFaresByAvailability = false,
  total,
}) => {
  const pricingTotal = useMemo(() => {
    if (useSelectedSeats) {
      return selectedSeatsTotal;
    }

    if (isBulkTicket) {
      if (monthlySelectedPlan > 1) {
        return selectedInstallmentsPlan?.total;
      }

      return globalBulkTotal;
    }

    if (displayPricingBeforeCheckout) {
      // If there are selected seats, calculate their total price
      if (selectedSeatsBeforeCheckout && selectedSeatsBeforeCheckout.length > 0) {
        return calculateSelectedSeatsTotal(
          selectedSeatsBeforeCheckout,
          total,
          useSeatBasedPricing,
          defaultPricing,
          mixDiscountFaresByAvailability,
        );
      }

      // Fall back to the regular total when the consumer doesn't provide a
      // pre-checkout total (e.g. PurchasePricingPassengerTrip), so the value is
      // never undefined.
      return purchaseTotalBeforeCheckout ?? total;
    }

    // Fallback to the regular total price
    return total;
  }, [
    displayPricingBeforeCheckout,
    globalBulkTotal,
    isBulkTicket,
    monthlySelectedPlan,
    purchaseTotalBeforeCheckout,
    selectedInstallmentsPlan,
    selectedSeatsBeforeCheckout,
    defaultPricing,
    mixDiscountFaresByAvailability,
    selectedSeatsTotal,
    total,
    useSeatBasedPricing,
    useSelectedSeats,
  ]);

  return pricingTotal;
};

export default usePricingTotal;
